import React from 'react';
import { NotificationProfile } from '../../../types';
import { Paper, Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { stepperStyles } from '../../styles';
import ConfirmationInfoItem from '../ConfirmationInfoItem/ConfirmationInfoItem';
import TimeSpanChipList from '../TimeSpans/TimeSpanList';
import { impactLevels } from '../EventTypes/EventType';

interface ProfileConfirmationProps {
  profile: NotificationProfile;
}

const ProfileConfirmation: React.FC<ProfileConfirmationProps> = ({
  profile,
}) => {
  const theme = useTheme();

  // Assuming index is not defined, you may want to remove key={index} and index usage.
  // If you want to support multiple profiles, you should map over them outside this component.

  return (
    <Paper
      elevation={2}
      sx={{
        ...stepperStyles.profileCard(theme, profile.enabled),
        p: 1.5,
        mb: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 0.5,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            color: theme.palette.text.primary,
            fontSize: '1rem',
            mr: 1,
            width: '100%',
            textAlign: 'center',
          }}
        >
          {profile.name || `Profile`}
          {profile.extRegions.length === 1 && <span> (1 Area) </span>}
          {profile.extRegions.length > 1 && (
            <span> ({profile.extRegions.length} Areas) </span>
          )}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: 1,
          mb: 1,
          alignItems: 'stretch',
        }}
      >
        {profile.timeSpans?.length > 0 && (
          <ConfirmationInfoItem
            title={'Time Spans'}
            content={<TimeSpanChipList timeSpans={profile.timeSpans} />}
          />
        )}
        {profile.additionalEmailAddresses?.length > 0 && (
          <ConfirmationInfoItem
            title={'Additional Emails'}
            content={profile.additionalEmailAddresses.join(', ')}
          />
        )}
      </Box>

      <Box sx={{ flexBasis: '100%' }}>
        {(Object.entries(profile.eventTypeImpacts || {}).length > 0 ||
          (profile.allImpactEventTypes &&
            profile.allImpactEventTypes.length > 0) ||
          (profile.regionlessEventTypes &&
            profile.regionlessEventTypes.length > 0)) && (
          <ConfirmationInfoItem
            title={'Event Types'}
            content={[
              ...Object.entries(profile.eventTypeImpacts || {}).map(
                ([key, value]) => {
                  // Find the display key that corresponds to the stored value
                  const entry = Array.from(impactLevels.entries()).find(
                    ([, mapValue]) => mapValue === value
                  );
                  const displayValue = entry ? entry[0] : value;
                  return `${key} (${displayValue})`;
                }
              ),
              ...(profile.allImpactEventTypes || []).map(
                (type) => `${type} (All)`
              ),
              ...(profile.regionlessEventTypes || []),
            ].join(', ')}
          />
        )}
      </Box>
    </Paper>
  );
};

export default ProfileConfirmation;
